import axios from 'axios'
import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'

import { secondaryFormatter } from './utils'
import UploadSection from './UploadSection'
import LastUploadedSection from '../components/LastUploadedSection'
import RegularColumnSelection from '../components/RegularColumnSelection'

import { hasAllRequiredColumnValues } from './SourceDataMethods'
import { makeStyles } from '@material-ui/core/styles'
import { API_URL } from '../../../config'
import { firstLetterUpper } from '../../../util/stringTransform'
import toast from 'react-hot-toast'
import { logError } from '../../../util/logger'

const useStyles = makeStyles(() => ({
  breakLine: {
    margin: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  root: {},
}))

const PurchaseOrdersSourceData = ({ uploadSummary, refetchData, setExpand }) => {
  const classes = useStyles()

  const currentColumnRef = uploadSummary?.columnRef
  const currentSheetSelected = uploadSummary?.sheetNameToSelect
  const currentUploadedFile = uploadSummary?.fileName
  const lastImported = uploadSummary?.lastImported
  const fileStructure = uploadSummary?.fileStructure
  const fileToken = uploadSummary?.fileToken
  const status = uploadSummary?.status
  const statusMessage = uploadSummary?.statusMessage

  const [sheet, setSheet] = useState(currentSheetSelected || '')
  const [currentColumnList, setCurrentColumnList] = useState(currentSheetSelected ? secondaryFormatter(fileStructure, currentSheetSelected) : [])
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(currentColumnRef?.purchaseOrderNumber || '')
  const [purchaseRequestNumber, setPurchaseRequestNumber] = useState(currentColumnRef?.purchaseRequestNumber || '')
  const [partNumber, setPartNumber] = useState(currentColumnRef?.partNumber || '')
  const [orderedQuantity, setOrderedQuantity] = useState(currentColumnRef?.orderedQuantity || '')
  const [receivedQuantity, setReceivedQuantity] = useState(currentColumnRef?.receivedQuantity || '')
  const [lineNumber, setLineNumber] = useState(currentColumnRef?.lineNumber || '')
  const [lineStatus, setLineStatus] = useState(currentColumnRef?.lineStatus || '')
  const [projectCode, setProjectCode] = useState(currentColumnRef?.projectCode || '')
  const [productCode, setProductCode] = useState(currentColumnRef?.productCode || '')
  const [disableSave, setDisableSave] = useState(true)

  const setColumnData = (inputSheet, sheet) => {
    setCurrentColumnList(secondaryFormatter(inputSheet.fileStructure, sheet))
  }

  const changeSheet = (sheetName) => {
    if (sheetName === '') {
      setCurrentColumnList([])
    } else setColumnData(uploadSummary, sheetName)
    setSheet(sheetName)
  }

  const criticalColumnConfig = [
    {
      label: 'Part Number',
      ref: partNumber,
      refSet: setPartNumber,
      required: true,
    },
    {
      label: 'PO Number',
      ref: purchaseOrderNumber,
      refSet: setPurchaseOrderNumber,
      required: true,
    },
    {
      label: 'PR Number',
      ref: purchaseRequestNumber,
      refSet: setPurchaseRequestNumber,
      required: false,
    },
    {
      label: 'Ordered Quantity',
      ref: orderedQuantity,
      refSet: setOrderedQuantity,
      required: true,
    },
    {
      label: 'Received Quantity',
      ref: receivedQuantity,
      refSet: setReceivedQuantity,
      required: true,
    },
    {
      label: 'PO Line Status',
      ref: lineStatus,
      refSet: setLineStatus,
      required: true,
    },
    {
      label: 'PO Line Number',
      ref: lineNumber,
      refSet: setLineNumber,
      required: true,
    },
    {
      label: 'Project Code',
      ref: projectCode,
      refSet: setProjectCode,
      required: false,
    },
    {
      label: 'Product Code',
      ref: productCode,
      refSet: setProductCode,
      required: false,
    },
  ]

  const allRequiredFieldsSelected = hasAllRequiredColumnValues(criticalColumnConfig)
  const handleFieldChange = (setStateFn, value) => {
    setStateFn(value)
    if (disableSave) setDisableSave(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const setPurchaseOrderColumnRef = `${API_URL}/api/dataSource/v1/setPurchaseOrderColumnRef`
    const data = {
      fileToken: fileToken,
      cleanImport: true,
      sheetName: sheet,
      columnRef: {
        partNumber,
        purchaseOrderNumber,
        purchaseRequestNumber,
        orderedQuantity,
        receivedQuantity,
        lineStatus,
        lineNumber,
        projectCode,
        productCode,
      },
    }

    const updatePurchaseOrdersColumnRef = axios
      .post(setPurchaseOrderColumnRef, data)
      .then(async () => {
        await refetchData()
        setDisableSave(true)
        setExpand(false)
      })
      .catch((err) => {
        toast.error('Somethign went wrong while trying to save your new selection. Please try again')
        logError(err.message, err.stack)
      })

    toast.promise(updatePurchaseOrdersColumnRef, {
      loading: <div>{`New Purchase Orders! This will replace your old purchase orders data.`}</div>,
      success: <b>Updated!</b>,
      error: <b>We had an issue saving your changes, please try again!</b>,
    })
  }

  return (
    <Grid container spacing={2} direction='column' className={classes.root}>
      <UploadSection uploadDataType='purchaseOrders' refetchSourceInfoFn={refetchData} setExpand={setExpand} uploadSummary={uploadSummary} />
      <hr className={classes.breakLine} />
      <LastUploadedSection
        fileName={currentUploadedFile}
        lastImported={lastImported}
        status={firstLetterUpper(status)}
        statusMessage={statusMessage}
        label={`PurchaseOrders`}
      />
      <hr className={classes.breakLine} />
      <RegularColumnSelection
        config={criticalColumnConfig}
        changeSheet={changeSheet}
        currentSheet={sheet}
        fileStructure={fileStructure}
        currentColumnList={currentColumnList}
        handleFieldChange={handleFieldChange}
        handleSubmit={handleSubmit}
        allRequiredFieldsSelected={allRequiredFieldsSelected}
        disableSave={disableSave}
      />
    </Grid>
  )
}

export default PurchaseOrdersSourceData
